import React, { useEffect, useState } from "react";
import { hasAuthParams, useAuth } from "react-oidc-context";
import { useDispatch } from "react-redux";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { Home } from "./pages/home";
import { RegisterMFA } from "./pages/register-mfa";
import { setUser } from "./app/reducer";
import { UserProfile } from "./pages/user-profile";
import { ManageDirects } from "./pages/manage-directs";
import { SetOtpPin } from "./pages/set-otp-pin";
import { SetBadgePin } from "./pages/set-badge-pin";
import {
  isAuthedUserManager,
  isOtpEnabledForAuthedUser,
} from "./utils/permissions";
import { RegisterKeyWizard } from "./pages/register-wizard";
import { useAppSelector } from "./app/selector";
import { NotFound } from "./pages/not-found";
import { getAppConfig } from "./services/config";
import { PagePaths } from "./lib/constants";
import { SplashScreen } from "./components/splash-screen";
import { Icon, Spinner } from "@amzn/awsui-components-react-v3";

function App() {
  const config = getAppConfig();
  const dispatch = useDispatch();
  const auth = useAuth();
  const otpEnabled = isOtpEnabledForAuthedUser(auth);
  const isManager = isAuthedUserManager(auth);
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const showRegistrationWizard = useAppSelector(
    (state) => state.app.showRegistrationWizard,
  );

  // automatically sign-in, passing the current path in OIDC state so we can
  // restore it after the redirect callback
  useEffect(() => {
    if (
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading &&
      !hasTriedSignin
    ) {
      auth.signinRedirect({
        state: {
          returnPath:
            window.location.pathname +
            window.location.search +
            window.location.hash,
        },
      });
      setHasTriedSignin(true);
    }
  }, [auth, hasTriedSignin]);

  if (
    auth.isLoading ||
    (auth.isLoading === undefined && auth.isAuthenticated === undefined)
  ) {
    let splashText;
    switch (auth.activeNavigator) {
      case "signinRedirect":
        splashText = "Signing you in...";
        break;
      case "signoutRedirect":
        splashText = "Signing you out...";
        break;
      default:
        splashText = "Please wait...";
    }
    return <SplashScreen icon={<Spinner size="large" />} text={splashText} />;
  }

  if (!auth.isAuthenticated) {
    if (hasTriedSignin && !auth.error) {
      return (
        <SplashScreen
          icon={<Spinner size="large" />}
          text="Signing you out..."
        />
      );
    }
    return (
      <SplashScreen
        icon={<Icon name="status-negative" size="big" variant="error" />}
        text="Unable to sign you in."
        subtext={auth.error && `Error: ${auth.error.message}`}
        subtextColor="text-status-error"
      />
    );
  }

  if (auth.isAuthenticated && !hasTriedSignin) {
    // If we've been in a signed-in state in the past, we're considering it as an attempted sign in.
    // This allows logout to clear the auth.isAuthenticated state without immediately triggering a new sign-in attempt
    setHasTriedSignin(true);
  }

  dispatch(setUser({ userToken: auth.user?.id_token ?? "" }));

  if (showRegistrationWizard) {
    return (
      <Router>
        <RegisterKeyWizard
          instructionsLink={config.registrationInstructionsLink}
        />
      </Router>
    );
  }

  const otpRoutes = () => {
    return (
      otpEnabled && (
        <>
          <Route path={PagePaths.SET_OTP_PIN} Component={SetOtpPin} />
        </>
      )
    );
  };

  const userManagementRoutes = () => {
    return (
      <>
        <Route path={PagePaths.USER_PROFILE} Component={UserProfile} />
        {isManager && (
          <Route path={PagePaths.MANAGE_DIRECTS} Component={ManageDirects} />
        )}
      </>
    );
  };

  return (
    <Router>
      <Routes>
        <Route
          path={PagePaths.HOME}
          element={<Home isManager={isManager} otpEnabled={otpEnabled} />}
        />
        <Route path={PagePaths.REGISTER_MFA} Component={RegisterMFA} />
        <Route path={PagePaths.SET_BADGE_PIN} Component={SetBadgePin} />
        {otpRoutes()}
        {userManagementRoutes()}
        <Route path="*" Component={NotFound} />
      </Routes>
    </Router>
  );
}

export default App;
